import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { PatternFormat } from 'react-number-format';
import Select from 'react-select';
import isEmail from 'validator/lib/isEmail';
import { publish } from '@/utils/publish';
import { HtmlParser, linkWithHttps, selectValue, toPhoneFormat } from '@/utils/helpers';
import { STATES } from '@/store/models/profile';
import tinycolor from 'tinycolor2';
import { withErrorBoundary } from '@/utils/errors';

import styles from './RecruitingPage.module.scss';
import sharedStyles from '@/components/CampaignPages/shared/sections.module.scss';
import HeroSection from '@/components/CampaignPages/shared/HeroSection';
import StorySection from '@/components/CampaignPages/shared/StorySection';
import ContactSection from '@/components/CampaignPages/shared/ContactSection';
import CtaFooterSection from '@/components/CampaignPages/shared/CtaFooterSection';
import FormValidator from '@/components/Forms/Validator';
import Notification from '@/components/Base/Notification';
import Submit from '@/components/Forms/Submit';
import { webpageContactRequest, applyToJobPostingRequest } from '@/store/actions/public.actions';

const LOCATION_TYPE_BADGE_STYLES = {
  in_person: { background: '#fce8ec', color: '#c8102e' },
  fully_remote: { background: '#e6f4ea', color: '#166534' },
  hybrid: { background: '#eef2f7', color: '#1a2a4a' },
  on_the_road: { background: '#fef9e6', color: '#92400e' }
};

const DEFAULT_STORY =
  "At American Senior Benefits, you get the earning power and flexibility of an independent agency with the training, infrastructure, and resources of a career agency — the best of both worlds, from a local office close to home. When you have questions or face challenges, we're always there to lend a hand. That's the culture we've built, and it's what sets us apart.";

const VIDEO_SRC = 'https://joinasb.com/includes/plugins/replicated_lp/videos/asb-join-video.mp4';

const RESUME_EXTENSIONS = ['pdf', 'doc', 'docx'];
const RESUME_MAX_SIZE = 10 * 1024 * 1024;

const STATE_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 6,
    borderWidth: '1.4px',
    borderColor: '#0e1d3e',
    boxShadow: 'none',
    '&:hover': { borderColor: '#0e1d3e' }
  }),
  valueContainer: base => ({ ...base, padding: '2px 12px' }),
  placeholder: base => ({ ...base, color: '#9ca3af', fontSize: 14 }),
  singleValue: base => ({ ...base, color: '#111827', fontSize: 14 }),
  input: base => ({ ...base, color: '#111827', fontSize: 14 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({ ...base, color: '#0e1d3e' }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? '#0e1d3e' : state.isFocused ? 'rgba(14, 29, 62, 0.08)' : '#fff',
    color: state.isSelected ? '#fff' : '#111827'
  }),
  menuPortal: base => ({ ...base, zIndex: 9999 })
};

const validateResumeFile = file => {
  if (!file) return null;

  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!RESUME_EXTENSIONS.includes(ext)) return 'Resume must be a PDF, DOC, or DOCX file';
  if (file.size > RESUME_MAX_SIZE) return 'Resume file is too large (max 10MB)';
  return null;
};

const resolveBrandColor = (color, fallback) =>
  !color || ['#ffffff', '#fff'].includes(color.toLowerCase()) ? fallback : color;

class RecruitingPage extends Component {
  initialFormState = {
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    comments: ''
  };

  initialApplyFormState = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    city: '',
    state: ''
  };

  resumeInputRef = React.createRef();

  state = {
    form: this.initialFormState,
    contactModalOpen: false,
    selectedJob: null,
    jobModalOpen: false,
    applyJob: null,
    applyForm: this.initialApplyFormState,
    applyErrors: {},
    applyResumeFile: null,
    applySubmitted: false
  };

  get corporation() {
    return this.props.info?.corporation || {};
  }

  get profile() {
    return this.props.info?.profile || {};
  }

  get colors() {
    const { corporation } = this;
    return {
      primary: resolveBrandColor(corporation.color_1, '#0b1d3d'),
      accent: resolveBrandColor(corporation.color_2, '#c8102e'),
      third: resolveBrandColor(corporation.color_3, '#1a2a4a'),
      heroText: '#ffffff',
      heroAccent: '#f9cbcc'
    };
  }

  get navBackground() {
    return tinycolor(this.colors.primary).darken(5).toString();
  }

  get jobPostings() {
    return this.props.jobPostings || [];
  }

  get storyVideo() {
    return { type: 'uploaded', url: VIDEO_SRC, thumbnailUrl: null };
  }

  get contact() {
    const { profile } = this;
    const email = this.props.info?.email || '';
    const parts = [profile.address_1];
    if (profile.address_2) parts.push(profile.address_2);
    if (profile.city) parts.push(profile.city);
    const stateZip = [profile.state, profile.zip_code].filter(Boolean).join(' ');
    if (stateZip) parts.push(stateZip);
    const address = parts.filter(Boolean).join(', ');
    return { address: profile.address_1 || profile.city ? address : '', phone: profile.phone, email };
  }

  formatJobLocation = job => {
    if (job.city && job.state) return ` · ${job.city}, ${job.state}`;
    if (job.city) return ` · ${job.city}`;
    if (job.state) return ` · ${job.state}`;
    if (job.location_type === 'fully_remote') return ' · Remote';
    if (job.location_type === 'on_the_road') return ' · Nationwide';
    return '';
  };

  openContactModal = () => this.setState({ contactModalOpen: true });
  closeContactModal = () => this.setState({ contactModalOpen: false });

  openJobModal = job => this.setState({ selectedJob: job, jobModalOpen: true });
  closeJobModal = () => this.setState({ jobModalOpen: false, selectedJob: null });

  scrollToJobs = () => {
    document.getElementById('browse-opportunities')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  handleApply = (job, closeModal = false) => {
    if (closeModal) this.closeJobModal();
    if (job.indeed_url) {
      window.open(linkWithHttps(job.indeed_url), '_blank');
    } else {
      this.openApplyModal(job);
    }
  };

  openApplyModal = job =>
    this.setState({
      applyJob: job,
      applyForm: this.initialApplyFormState,
      applyErrors: {},
      applyResumeFile: null,
      applySubmitted: false
    });

  closeApplyModal = () => this.setState({ applyJob: null });

  onApplyFieldChange = event => {
    const { name, value } = event.target;
    this.setState(prev => ({
      applyForm: { ...prev.applyForm, [name]: value },
      applyErrors: { ...prev.applyErrors, [name]: false }
    }));
  };

  onApplyStateChange = option => {
    this.setState(prev => ({ applyForm: { ...prev.applyForm, state: option?.value || '' } }));
  };

  onApplyPhoneBlur = event => {
    const value = toPhoneFormat(event.target.value);
    this.setState(prev => ({ applyForm: { ...prev.applyForm, phone: value } }));
  };

  onApplyResumeChange = event => {
    const file = event.target.files?.[0] || null;
    const error = validateResumeFile(file);
    this.setState(prev => ({
      applyResumeFile: error ? null : file,
      applyErrors: { ...prev.applyErrors, resume: error }
    }));
    if (error) event.target.value = '';
  };

  validateApplyForm = () => {
    const { applyForm, applyResumeFile } = this.state;
    const errors = {
      firstName: !applyForm.firstName.trim(),
      lastName: !applyForm.lastName.trim(),
      email: !applyForm.email.trim() || !isEmail(applyForm.email),
      resume: validateResumeFile(applyResumeFile)
    };
    this.setState({ applyErrors: errors });
    return !Object.values(errors).some(Boolean);
  };

  onApplySubmit = async (e, callback) => {
    e.preventDefault();

    if (!this.validateApplyForm()) return callback?.();

    const { applyForm, applyJob, applyResumeFile } = this.state;
    const formDataBody = new FormData();
    formDataBody.append('first_name', applyForm.firstName.trim());
    formDataBody.append('last_name', applyForm.lastName.trim());
    formDataBody.append('email', applyForm.email.trim().toLowerCase());
    if (applyForm.phone) formDataBody.append('phone', applyForm.phone);
    if (applyForm.city) formDataBody.append('city', applyForm.city.trim());
    if (applyForm.state) formDataBody.append('state', applyForm.state.trim());
    formDataBody.append('job_title', applyJob.title);
    if (applyJob.id) formDataBody.append('job_posting_id', applyJob.id);
    if (applyResumeFile) formDataBody.append('resume', applyResumeFile);

    try {
      await this.props.applyToJobPostingRequest({
        domain: this.props.webpageUsername,
        formDataBody
      });
      this.setState({ applySubmitted: true });
    } catch (ex) {
      publish('error', ex);
    }

    callback?.();
  };

  validateOnChange = event => {
    const input = event.target;
    const form = input.form;
    const result = FormValidator.validate(input);

    this.setState({
      [form.name]: {
        ...this.state[form.name],
        [input.name]: input.value,
        errors: {
          ...this.state[form.name].errors,
          [input.name]: result
        }
      }
    });
  };

  hasError = (formName, inputName, method) => this.state[formName]?.errors?.[inputName]?.[method];

  renderFieldError = (fieldName, rules = ['required']) => {
    const messages = {
      required: 'This field is required',
      email: 'Invalid email address format'
    };

    for (const rule of rules) {
      if (this.hasError('form', fieldName, rule)) {
        return <p className={styles.contactFieldError}>{messages[rule]}</p>;
      }
    }

    return null;
  };

  onSubmit = async (e, callback) => {
    const form = e.target.form;
    const inputs = [...form.elements].filter(i => ['INPUT', 'SELECT', 'TEXTAREA'].includes(i.nodeName));
    const { errors, hasError } = FormValidator.bulkValidate(inputs);

    this.setState({ [form.name]: { ...this.state[form.name], errors } });
    e.preventDefault();

    if (hasError) return callback?.();

    const { first_name, last_name, email, phone, comments } = this.state[form.name];

    await this.props
      .webpageContactRequest({
        domain: this.props.webpageUsername,
        body: {
          first_name: first_name.trim(),
          last_name: last_name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          note: comments.trim()
        }
      })
      .then(() => {
        this.setState({ form: this.initialFormState, contactModalOpen: false });
        publish('success', 'Thank you! We will be in touch soon.');
      })
      .catch(res => publish('error', res));

    callback?.();
  };

  renderJobDetailList = (title, items) => {
    if (!items?.length) return null;

    return (
      <div className={styles.jobDetailSection}>
        <h4 className={styles.jobDetailSectionTitle}>{title}</h4>
        <ul className={styles.jobDetailList} style={{ '--bullet-color': this.colors.accent }}>
          {items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      </div>
    );
  };

  get jobsSection() {
    const { corporation } = this;
    const { accent, third } = this.colors;

    return (
      <section id="browse-opportunities" className={styles.jobs}>
        <div className={sharedStyles.container}>
          <p className={sharedStyles.sectionLabel} style={{ color: accent }}>
            NOW HIRING
          </p>
          <h2 className={sharedStyles.sectionHeading} style={{ color: third }}>
            Browse Our Career Opportunities
          </h2>
          <div className={styles.jobsDivider} style={{ background: accent }} />

          <div className={styles.jobsList}>
            {this.jobPostings.map(job => (
              <div key={job.id} className={styles.jobCard} onClick={() => this.openJobModal(job)}>
                <div className={styles.jobCardMain}>
                  <div className={styles.jobCardInfo}>
                    <h3 className={styles.jobTitle} style={{ color: third }} onClick={() => this.openJobModal(job)}>
                      {job.title}
                    </h3>
                    <p className={styles.jobCompany}>
                      {job.company_name || corporation.name || 'American Senior Benefits'}
                      {this.formatJobLocation(job)}
                    </p>
                    <div className={styles.jobBadges}>
                      <span className={styles.badgeEmployment}>{job.employment_type?.toUpperCase()}</span>
                      <span className={styles.badge} style={LOCATION_TYPE_BADGE_STYLES[job.location_type]}>
                        {job.location_type?.replaceAll('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    <p className={styles.jobShortDesc}>{job.short_description}</p>
                  </div>
                  <div className={styles.jobCardActions}>
                    <button
                      className={sharedStyles.btnApply}
                      style={{ background: accent }}
                      onClick={() => this.handleApply(job)}
                    >
                      APPLY NOW
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  get contactModal() {
    if (!this.state.contactModalOpen) return null;

    const { first_name, last_name, phone, email, comments } = this.state.form;
    const { accent } = this.colors;

    return (
      <div className={styles.modalOverlay} onClick={this.closeContactModal}>
        <div className={classNames(styles.modal, styles.contactModal)} onClick={e => e.stopPropagation()}>
          <div className={styles.contactModalHeader}>
            <h3 className={styles.contactModalTitle}>Want to learn more about our career opportunities?</h3>
            <p className={styles.contactModalSubtitle}>
              Fill out the form below and someone from our team will reach out to you directly.
            </p>
          </div>

          <form autoComplete="off" name="form" id="recruit-form" className={styles.contactModalForm}>
            <div className={styles.contactFormRow}>
              <div className={styles.contactField}>
                <input
                  name="first_name"
                  onChange={this.validateOnChange}
                  className={classNames(
                    styles.contactInput,
                    this.hasError('form', 'first_name', 'required') && styles.contactInputError
                  )}
                  data-validate='["required"]'
                  placeholder="First Name*"
                  type="text"
                  value={first_name}
                />
                {this.renderFieldError('first_name')}
              </div>
              <div className={styles.contactField}>
                <input
                  name="last_name"
                  onChange={this.validateOnChange}
                  className={classNames(
                    styles.contactInput,
                    this.hasError('form', 'last_name', 'required') && styles.contactInputError
                  )}
                  data-validate='["required"]'
                  placeholder="Last Name*"
                  type="text"
                  value={last_name}
                />
                {this.renderFieldError('last_name')}
              </div>
            </div>
            <div className={styles.contactFormRow}>
              <div className={styles.contactField}>
                <input
                  name="email"
                  onChange={this.validateOnChange}
                  className={classNames(
                    styles.contactInput,
                    (this.hasError('form', 'email', 'required') || this.hasError('form', 'email', 'email')) &&
                      styles.contactInputError
                  )}
                  data-validate='["required","email"]'
                  placeholder="Email*"
                  type="email"
                  value={email}
                />
                {this.renderFieldError('email', ['required', 'email'])}
              </div>
              <div className={styles.contactField}>
                <PatternFormat
                  name="phone"
                  format="(###) ###-####"
                  placeholder="Phone"
                  onChange={this.validateOnChange}
                  className={styles.contactInput}
                  value={phone}
                />
              </div>
            </div>
            <textarea
              name="comments"
              onChange={this.validateOnChange}
              className={styles.contactTextarea}
              placeholder="Comments"
              rows={5}
              value={comments}
            />

            <Submit
              backgroundColor={accent}
              label="SUBMIT"
              className={styles.contactModalSubmit}
              onSubmit={this.onSubmit}
            />

            <p className={styles.contactDisclaimer}>
              Submitting this form constitutes my written consent to receive marketing communications and related calls
              and/or text messages from American Senior Benefits and its affiliates including those made via autodialer,
              automated technology and/or prerecorded or artificial voice message at the above phone number, even if my
              number is registered on a Do Not Call Registry. I affirm that I am the regular user of the number
              provided. I understand that message and data rates may apply, that my consent is not a condition of
              purchase and that I can revoke my consent at any time.
            </p>
          </form>
        </div>
      </div>
    );
  }

  get applyModal() {
    const { applyJob, applyForm, applyErrors, applyResumeFile, applySubmitted } = this.state;
    if (!applyJob) return null;

    const { accent } = this.colors;

    return (
      <div className={styles.modalOverlay} onClick={this.closeApplyModal}>
        <div className={classNames(styles.modal, styles.contactModal)} onClick={e => e.stopPropagation()}>
          {applySubmitted ? (
            <div className={styles.contactModalHeader}>
              <h3 className={styles.contactModalTitle}>Application Sent!</h3>
              <p className={styles.contactModalSubtitle}>
                Thanks for applying to {applyJob.title}. We'll be in touch soon.
              </p>
            </div>
          ) : (
            <>
              <div className={styles.contactModalHeader}>
                <h3 className={styles.contactModalTitle}>Apply for {applyJob.title}</h3>
                <p className={styles.contactModalSubtitle}>
                  Fill out the form below to apply directly — no need to leave this page.
                </p>
              </div>

              <form autoComplete="off" name="applyForm" className={styles.contactModalForm}>
                <div className={styles.contactFormRow}>
                  <div className={styles.contactField}>
                    <label className={styles.contactFieldLabel} htmlFor="apply-first-name">
                      First Name*
                    </label>
                    <input
                      id="apply-first-name"
                      name="firstName"
                      onChange={this.onApplyFieldChange}
                      className={classNames(styles.contactInput, applyErrors.firstName && styles.contactInputError)}
                      type="text"
                      value={applyForm.firstName}
                    />
                    {applyErrors.firstName && <p className={styles.contactFieldError}>This field is required</p>}
                  </div>
                  <div className={styles.contactField}>
                    <label className={styles.contactFieldLabel} htmlFor="apply-last-name">
                      Last Name*
                    </label>
                    <input
                      id="apply-last-name"
                      name="lastName"
                      onChange={this.onApplyFieldChange}
                      className={classNames(styles.contactInput, applyErrors.lastName && styles.contactInputError)}
                      type="text"
                      value={applyForm.lastName}
                    />
                    {applyErrors.lastName && <p className={styles.contactFieldError}>This field is required</p>}
                  </div>
                </div>
                <div className={styles.contactField}>
                  <label className={styles.contactFieldLabel} htmlFor="apply-email">
                    Email Address*
                  </label>
                  <input
                    id="apply-email"
                    name="email"
                    onChange={this.onApplyFieldChange}
                    className={classNames(styles.contactInput, applyErrors.email && styles.contactInputError)}
                    type="email"
                    value={applyForm.email}
                  />
                  {applyErrors.email && <p className={styles.contactFieldError}>Invalid email address format</p>}
                </div>
                <div className={styles.contactField}>
                  <label className={styles.contactFieldLabel} htmlFor="apply-phone">
                    Phone Number
                  </label>
                  <input
                    id="apply-phone"
                    name="phone"
                    maxLength={17}
                    onChange={this.onApplyFieldChange}
                    onBlur={this.onApplyPhoneBlur}
                    className={styles.contactInput}
                    type="text"
                    value={applyForm.phone}
                  />
                </div>
                <div className={styles.contactFormRow}>
                  <div className={styles.contactField}>
                    <label className={styles.contactFieldLabel} htmlFor="apply-city">
                      City
                    </label>
                    <input
                      id="apply-city"
                      name="city"
                      onChange={this.onApplyFieldChange}
                      className={styles.contactInput}
                      type="text"
                      value={applyForm.city}
                    />
                  </div>
                  <div className={styles.contactField}>
                    <label className={styles.contactFieldLabel} htmlFor="apply-state">
                      State
                    </label>
                    <Select
                      inputId="apply-state"
                      name="state"
                      options={STATES}
                      value={selectValue(STATES, applyForm.state)}
                      onChange={this.onApplyStateChange}
                      styles={STATE_SELECT_STYLES}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                    />
                  </div>
                </div>

                <div className={styles.contactField}>
                  <label className={styles.contactFieldLabel}>Resume</label>
                  <div className={styles.resumeUpload}>
                    <button
                      type="button"
                      className={styles.resumeUploadButton}
                      style={{ borderColor: accent, color: accent }}
                      onClick={() => this.resumeInputRef.current?.click()}
                    >
                      Upload Resume
                    </button>
                    <span className={styles.resumeFileName}>
                      {applyResumeFile ? applyResumeFile.name : 'No file chosen'}
                    </span>
                    <input
                      ref={this.resumeInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className={styles.resumeInputHidden}
                      onChange={this.onApplyResumeChange}
                    />
                  </div>
                  {applyErrors.resume && <p className={styles.contactFieldError}>{applyErrors.resume}</p>}
                </div>

                <Submit
                  backgroundColor={accent}
                  label="Submit Application"
                  className={styles.contactModalSubmit}
                  onSubmit={this.onApplySubmit}
                />
              </form>
            </>
          )}
        </div>
      </div>
    );
  }

  get jobModal() {
    const job = this.state.selectedJob;
    if (!this.state.jobModalOpen || !job) return null;

    const { primary, accent } = this.colors;

    return (
      <div className={styles.modalOverlay} onClick={this.closeJobModal}>
        <div className={classNames(styles.modal, styles.jobDetailModal)} onClick={e => e.stopPropagation()}>
          <div className={styles.jobDetailHeader} style={{ background: primary }}>
            <p className={styles.jobDetailLabel}>NOW HIRING</p>
            <h3 className={styles.jobDetailTitle}>{job.title?.toUpperCase()}</h3>
            <div className={styles.jobDetailBadges}>
              <span className={styles.badgeLight}>{job.employment_type.toUpperCase()}</span>
              <span className={styles.badgeLight}>{job.location_type?.replaceAll('_', ' ').toUpperCase()}</span>
            </div>
          </div>

          <div className={styles.jobDetailBody}>
            {job.description && (
              <div className={styles.jobDetailSection}>
                <h4 className={styles.jobDetailSectionTitle}>DESCRIPTION</h4>
                <p>{job.description}</p>
              </div>
            )}

            <div className={styles.jobDetailRow}>
              {job.time_commitment && (
                <div className={styles.jobDetailCol}>
                  <h4 className={styles.jobDetailSectionTitle}>TIME COMMITMENT</h4>
                  <p>{job.time_commitment}</p>
                </div>
              )}
              {job.work_schedule && (
                <div className={styles.jobDetailCol}>
                  <h4 className={styles.jobDetailSectionTitle}>WORK SCHEDULE</h4>
                  <p>{job.work_schedule}</p>
                </div>
              )}
            </div>

            {this.renderJobDetailList('REQUIREMENTS', job.requirements)}
            {this.renderJobDetailList('RESPONSIBILITIES', job.responsibilities)}
            {this.renderJobDetailList('BENEFITS & COMPENSATION', job.benefits_and_compensation)}
          </div>

          <div className={styles.jobDetailActions}>
            <button
              className={sharedStyles.btnApply}
              style={{ background: accent }}
              onClick={() => this.handleApply(job, true)}
            >
              APPLY NOW
            </button>
            <button
              className={sharedStyles.btnOutlineDark}
              style={{ borderColor: primary, color: primary }}
              onClick={() => {
                this.closeJobModal();
                this.openContactModal();
              }}
            >
              HAVE QUESTIONS?
            </button>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { corporation } = this;
    const description = corporation.description ? HtmlParser(corporation.description) : DEFAULT_STORY;

    return (
      <div>
        <HeroSection
          background={this.colors.primary}
          navBackground={this.navBackground}
          colors={this.colors}
          logoUrl={corporation.logo?.url}
          corporationName={corporation.name}
          headline={{ main: 'Where Insurance Careers', accent: 'Come to Grow.' }}
          subtitle="You don't build alone here. Behind every agent is a team as invested in your success as you are — giving you the freedom, the tools, and the backing to build a book of business that's truly yours."
          onViewOpportunities={this.scrollToJobs}
          onContact={this.openContactModal}
        />

        <StorySection
          colors={this.colors}
          heading="Putting People Back Into the People Business"
          body={description}
          video={this.storyVideo}
          videoThumbnailSlot={
            corporation.logo?.url ? (
              <img className={styles.videoThumbnailLogo} src={corporation.logo.url} alt={corporation.name} />
            ) : null
          }
        />

        {this.jobsSection}

        <ContactSection colors={this.colors} contact={this.contact} />

        <CtaFooterSection
          background={this.colors.primary}
          colors={this.colors}
          onViewOpportunities={this.scrollToJobs}
          onContact={this.openContactModal}
        />

        {this.contactModal}
        {this.jobModal}
        {this.applyModal}
        <Notification />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  info: state.public.info,
  jobPostings: state.public.info?.profile?.job_postings
});

export default connect(mapStateToProps, { webpageContactRequest, applyToJobPostingRequest })(
  withErrorBoundary(RecruitingPage)
);
